@file:OptIn(ExperimentalTime::class)

package org.lortex.lines.common

import androidx.compose.runtime.Immutable
import androidx.compose.runtime.Stable
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DateTimePeriod
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atTime
import kotlinx.datetime.daysUntil
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.yearsUntil
import kotlinx.serialization.Serializable
import org.lortex.lines.common.GregorianDate
import kotlin.math.absoluteValue
import kotlin.math.floor
import kotlin.math.pow
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

@Stable
@Immutable
data class GregorianDateTime(
    val date: GregorianDate,
    val time: LocalTime = LocalTime(0, 0, 0),
) : Comparable<GregorianDateTime> {
    override fun compareTo(other: GregorianDateTime): Int {
        val dateComp = this.date.compareTo(other.date)
        if (dateComp != 0) return dateComp
        return this.time.compareTo(other.time)
    }

    @OptIn(ExperimentalTime::class)
    fun toInstant(timeZone: TimeZone): Instant {
        if (date.year !in LOCAL_DATE_WORKING_RANGE) {
            val days = (date.year * 365.2425f).toLong()
            return Instant.fromEpochSeconds(days * 24 * 3600)
        } else {
            val localDateTime = date.toLocalDate()!!.atTime(time)
            return localDateTime.toInstant(timeZone)
        }
    }

    fun toLongInstant(timeZone: TimeZone): LongInstant {
        return LongInstant.fromGregorianDateTime(this, timeZone)
    }

    fun toLocalDateTime(): LocalDateTime? {
        return date.toLocalDate()?.atTime(time)
    }

    fun toLocalDate(): LocalDate? {
        return date.toLocalDate()
    }

    operator fun plus(datePeriod: DatePeriod): GregorianDateTime {
        val newDate = this.date + datePeriod
        return GregorianDateTime(newDate, this.time)
    }

    operator fun minus(datePeriod: DatePeriod): GregorianDateTime {
        val newDate = this.date - datePeriod
        return GregorianDateTime(newDate, this.time)
    }
}

val DISTANT_DAYS = (Instant.DISTANT_PAST.epochSeconds / 3600 / 24).absoluteValue

val zeroOffset = LocalDate(1, 1, 1).toEpochDays()

// like kotlinx datetime but supports very large negative and positive years
@Serializable
@Stable
@Immutable
data class GregorianDate(
    val year: Long,
    val month: Int = 1,
    val day: Int = 1,
) : Comparable<GregorianDate> {


    constructor(localDate: LocalDate) : this(
        localDate.year.toLong(),
        localDate.monthNumber,
        localDate.dayOfMonth
    )

    override fun toString(): String {
        return "$day/$month/$year"
    }

    fun atTime(hour: Int, minute: Int): GregorianDateTime {
        return GregorianDateTime(this, LocalTime(hour, minute))
    }

    fun yearsUntil(other: GregorianDate): Long {
        this.toLocalDate()?.let { thisLocalDate ->
            other.toLocalDate()?.let { otherLocalDate ->
                return thisLocalDate.yearsUntil(otherLocalDate).toLong()
            }
        }

        return other.year - this.year
    }

    fun renderYear(): String {
        if (year < 0) {
            if (year > -1000000) {
                return "BC ${-year}"
            } else if (year > -1000000000) {
                return "BC ${(-year / 1000000f).format(1)}M"
            } else {
                return "BC ${(-year / 1000000000f).format(1)}G"
            }
        } else if (year >= 1_000_000_000f) {
            return "${(year / 1_000_000_000f).format(1)}G"
        } else if (year >= 1_000_000f) {
            return "${(year / 1_000_000f).format(1)}M"
        } else {

            return year.toString()
        }
    }

    fun daysUntil(other: GregorianDate): Long {
        if (this.year !in LOCAL_DATE_WORKING_RANGE ||
            other.year !in LOCAL_DATE_WORKING_RANGE
        ) {
            val daysThis = this.year * 365.2425f
            val daysOther = other.year * 365.2425f
            return (daysOther - daysThis).toLong()
        } else {
            val localDateThis = LocalDate(this.year.toInt(), this.month, this.day)
            val localDateOther = LocalDate(other.year.toInt(), other.month, other.day)
            return localDateThis.daysUntil(localDateOther).toLong()
        }
    }

    override fun compareTo(other: GregorianDate): Int {
        return when {
            this.year != other.year -> this.year.compareTo(other.year)
            this.month != other.month -> this.month.compareTo(other.month)
            else -> this.day.compareTo(other.day)
        }
    }

    fun toLocalDate(): LocalDate? {
        if (this.year !in LOCAL_DATE_WORKING_RANGE) return null
        return LocalDate(this.year.toInt(), this.month, this.day)
    }

    operator fun plus(datePeriod: DatePeriod): GregorianDate {
        val localDate = this.toLocalDate() ?: return GregorianDate(
            year = year + datePeriod.years,
            month = month,
            day = day
        )
        val newLocalDate = localDate + datePeriod
        return GregorianDate(newLocalDate)
    }

    operator fun minus(datePeriod: DatePeriod): GregorianDate {
        val localDate = this.toLocalDate() ?: return GregorianDate(
            year = year - datePeriod.years,
            month = month,
            day = day
        )
        val newLocalDate = localDate - datePeriod
        return GregorianDate(newLocalDate)
    }

    private fun exactToEpochDays(): Long {
        val m = month.toLong()
        val d = day.toLong()
        val y = if (m <= 2) year - 1 else year          // shift so year starts in March

        val era = (if (y >= 0) y else y - 399) / 400
        val yoe = y - era * 400                          // year-of-era [0, 399]
        val mp = if (m > 2) m - 3 else m + 9             // month index with March = 0
        val doy = (153 * mp + 2) / 5 + d - 1             // day-of-year [0, 365]
        val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy  // day-of-era [0, 146096]

        return era * 146097 + doe - 719468L              // shift back to 1970-01-01 epoch

    }

    fun toEpochDays(): Long {
        if (this.year !in LOCAL_DATE_WORKING_RANGE) {
            return exactToEpochDays()
        } else {
            val localDate = LocalDate(this.year.toInt(), this.month, this.day)
            return localDate.toEpochDays()
        }
    }

    private fun exactDayOfYear(): Int {
        val startOfYear = GregorianDate(year, 1, 1).toEpochDays()
        return (toEpochDays() - startOfYear + 1).toInt()
    }

    fun dayOfYear(): Int {
        if (this.year !in LOCAL_DATE_WORKING_RANGE) {
            return exactDayOfYear()
        } else {
            val localDate = LocalDate(this.year.toInt(), this.month, this.day)
            return localDate.dayOfYear
        }
    }

    fun toDaysSinceZero(): Long {
        return this.toEpochDays() - zeroOffset
    }

    companion object {

        @OptIn(ExperimentalTime::class)
        fun fromDaysSinceZero(days: Long): GregorianDate {
            return fromDaysSinceEpoch(days + zeroOffset)
        }

        private fun exactFromEpochDays(days: Long): GregorianDate {
            val z =
                days + 719468L                                        // shift epoch to 0000-03-01
            val era = (if (z >= 0) z else z - 146096) / 146097
            val doe = z - era * 146097                                    // day-of-era [0, 146096]
            val yoe = (doe - doe / 1460 + doe / 36524 - doe / 146096) / 365 // year-of-era [0, 399]
            val y = yoe + era * 400
            val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)             // day-of-year [0, 365]
            val mp = (5 * doy + 2) / 153                                  // [0, 11]
            val day = (doy - (153 * mp + 2) / 5 + 1).toInt()              // [1, 31]
            val month = (mp + if (mp < 10) 3 else -9).toInt()             // [1, 12]
            val year = if (month <= 2) y + 1 else y

            return GregorianDate(year, month, day)
        }

        fun fromDaysSinceEpoch(days: Long): GregorianDate {
            return if (days.absoluteValue > DISTANT_DAYS) {
                exactFromEpochDays(days)
            } else {
                val localDate = LocalDate.fromEpochDays(days.toInt())
                GregorianDate(
                    year = localDate.year.toLong(),
                    month = localDate.monthNumber,
                    day = localDate.dayOfMonth
                )
            }
        }

    }
}

fun dayOfYearToMonthAndDay(dayOfYear: Int, isLeapYear: Boolean): Pair<Int, Int> {
    if (dayOfYear < 30) {
        return Pair(1, dayOfYear + 1)
    } else if (dayOfYear < 59 + if (isLeapYear) 1 else 0) {
        return Pair(2, dayOfYear - 30 + 1)
    } else if (dayOfYear < 90 + if (isLeapYear) 1 else 0) {
        return Pair(3, dayOfYear - (59 + if (isLeapYear) 1 else 0) + 1)
    } else if (dayOfYear < 120 + if (isLeapYear) 1 else 0) {
        return Pair(4, dayOfYear - (90 + if (isLeapYear) 1 else 0) + 1)
    } else if (dayOfYear < 151 + if (isLeapYear) 1 else 0) {
        return Pair(5, dayOfYear - (120 + if (isLeapYear) 1 else 0) + 1)
    } else if (dayOfYear < 181 + if (isLeapYear) 1 else 0) {
        return Pair(6, dayOfYear - (151 + if (isLeapYear) 1 else 0) + 1)
    } else if (dayOfYear < 212 + if (isLeapYear) 1 else 0) {
        return Pair(7, dayOfYear - (181 + if (isLeapYear) 1 else 0) + 1)
    } else if (dayOfYear < 243 + if (isLeapYear) 1 else 0) {
        return Pair(8, dayOfYear - (212 + if (isLeapYear) 1 else 0) + 1)
    } else if (dayOfYear < 273 + if (isLeapYear) 1 else 0) {
        return Pair(9, dayOfYear - (243 + if (isLeapYear) 1 else 0) + 1)
    } else if (dayOfYear < 304 + if (isLeapYear) 1 else 0) {
        return Pair(10, dayOfYear - (273 + if (isLeapYear) 1 else 0) + 1)
    } else if (dayOfYear < 334 + if (isLeapYear) 1 else 0) {
        return Pair(11, dayOfYear - (304 + if (isLeapYear) 1 else 0) + 1)
    } else {
        return Pair(12, dayOfYear - (334 + if (isLeapYear) 1 else 0) + 1)
    }
}

fun LocalDate.toGregorianDate(): GregorianDate {
    return GregorianDate(this)
}

data class LongInstant(val value: Long, val nanosecondAdjustment: Long = 0) :
    Comparable<LongInstant> {
    constructor(instant: Instant) : this(instant.epochSeconds, instant.nanosecondsOfSecond.toLong())

    override fun toString(): String {
        return if (nanosecondAdjustment != 0L) {
            if (value >= 0L) {
                "I:$value.${nanosecondAdjustment.toString().padStart(9, '0')}"
            } else {
                if (value == -1L) {
                    "I:-0.${
                        (1_000_000_000 - nanosecondAdjustment).toString().padStart(9, '0')
                    }"
                } else {
                    "I:${value + 1}.${
                        (1_000_000_000 - nanosecondAdjustment).toString().padStart(9, '0')
                    }"
                }
            }
        } else {
            "I:$value"
        }
    }

    override fun compareTo(other: LongInstant): Int {
        val secComp = this.value.compareTo(other.value)
        if (secComp != 0) return secComp
        return this.nanosecondAdjustment.compareTo(other.nanosecondAdjustment)
    }

    operator fun div(other: Float): LongInstant {
        val value = this.value / other
        val remainder = this.value % other

        val nanos = this.nanosecondAdjustment / other + remainder * 1_000_000_000 / other
        val nanosRem = nanos.rem(1_000_000_000).toLong()
        return LongInstant(value.toLong() + (nanos.toLong() - nanosRem) / 1_000_000_000, nanosRem)
    }

    operator fun div(other: Double): LongInstant {
        val value = this.value / other
        val remainder = this.value % other

        val nanos = this.nanosecondAdjustment / other + remainder * 1_000_000_000 / other
        val nanosRem = nanos.rem(1_000_000_000).toLong()
        return LongInstant(value.toLong() + (nanos.toLong() - nanosRem) / 1_000_000_000, nanosRem)
    }

    operator fun div(other: Int): LongInstant {
        val value = this.value / other
        val remainder = this.value % other
        val nanos = this.nanosecondAdjustment / other + remainder * 1_000_000_000 / other
        val nanosRem = nanos.rem(1_000_000_000)
        return LongInstant(value + (nanos - nanosRem) / 1_000_000_000, nanosRem)
    }

    operator fun div(other: Long): LongInstant {
        val value = this.value / other
        val remainder = this.value % other
        val nanos = this.nanosecondAdjustment / other + remainder * 1_000_000_000 / other
        val nanosRem = nanos.rem(1_000_000_000)
        return LongInstant(value + (nanos - nanosRem) / 1_000_000_000, nanosRem)
    }

    operator fun div(other: LongInstant): Long {

        val maxSecondsBeforeOverflow = Long.MAX_VALUE / 1_000_000_000

        if (other.value >= maxSecondsBeforeOverflow / 2) { // add some leeway
            return this.value / other.value
        }

        val divisorNanos = other.nanosecondAdjustment + other.value * 1_000_000_000


        if (this.value < maxSecondsBeforeOverflow / 2) {
            val result = (this.value * 1_000_000_000 + this.nanosecondAdjustment) / divisorNanos
            return result
        }


        if (other.value == 0L && other.nanosecondAdjustment == 0L) {
            throw IllegalArgumentException("Division by zero")
        }

        // TODO: better precision computation
        return this.value / other.value
    }

    /*

    (a * 1e9) / (b * 1e9 + c)

     */

    operator fun times(other: Long): LongInstant {

        val nano = this.nanosecondAdjustment * other
        val remainder = nano.mod(1_000_000_000)
        val secondsCarry = (nano - remainder) / 1_000_000_000
        return LongInstant(
            this.value * other + secondsCarry,
            remainder.toLong()
        )

    }

    operator fun times(other: Int): LongInstant {
        val nano = this.nanosecondAdjustment * other
        val remainder = nano.mod(1_000_000_000)
        val secondsCarry = (nano - remainder) / 1_000_000_000
        return LongInstant(
            this.value * other + secondsCarry,
            remainder.toLong()
        )
    }

    operator fun times(other: Float): LongInstant {
        val secondsAndRest = this.value * other
        val seconds = floor(secondsAndRest)

        val nano = this.nanosecondAdjustment * other + (secondsAndRest - seconds) * 1_000_000_000

        val remainder = nano.toInt().mod(1_000_000_000)
        val secondsCarry = ((nano - remainder) / 1_000_000_000).toLong()
        return LongInstant(
            seconds.toLong() + secondsCarry,
            remainder.toLong()
        )
    }

    operator fun times(other: Double): LongInstant {
        val secondsAndRest = this.value * other
        val seconds = floor(secondsAndRest)

        val nano = this.nanosecondAdjustment * other + (secondsAndRest - seconds) * 1_000_000_000

        val remainder = nano.toInt().mod(1_000_000_000)
        val secondsCarry = ((nano - remainder) / 1_000_000_000).toLong()
        return LongInstant(
            seconds.toLong() + secondsCarry,
            remainder.toLong()
        )
    }

    fun toGregorianDateTime(timeZone: TimeZone): GregorianDateTime {
        if (value !in Instant.DISTANT_PAST.epochSeconds..Instant.DISTANT_FUTURE.epochSeconds) {
            val days = (value / 3600 / 24)
            return GregorianDateTime(date = GregorianDate.fromDaysSinceEpoch(days))
        } else {
            val instant = Instant.fromEpochSeconds(value, nanosecondAdjustment)
            val localDateTime = instant.toLocalDateTime(timeZone)
            return GregorianDateTime(
                date = GregorianDate(localDateTime.date),
                time = localDateTime.time,
            )
        }
    }

    fun asFloat(): Float {
        return value + nanosecondAdjustment / 1_000_000_000f
    }

    operator fun plus(instant: LongInstant): LongInstant {
        val nano = this.nanosecondAdjustment + instant.nanosecondAdjustment
        val remainder = nano.mod(1_000_000_000)
        val secondsCarry = (nano - remainder) / 1_000_000_000
        return LongInstant(
            this.value + instant.value + secondsCarry,
            remainder.toLong()
        )
    }

    operator fun minus(instant: LongInstant): LongInstant {

        val nano = this.nanosecondAdjustment - instant.nanosecondAdjustment
        val remainder = nano.mod(1_000_000_000)
        val secondsCarry = (nano - remainder) / 1_000_000_000

        return LongInstant(
            this.value - instant.value + secondsCarry,
            remainder.toLong()
        )
    }

    companion object {
        val yearApprox: LongInstant = LongInstant(365 * 86400)
        val monthApprox: LongInstant = LongInstant(30 * 86400)
        val day: LongInstant = LongInstant(86400)
        val hour: LongInstant = LongInstant(3600)
        val minute: LongInstant = LongInstant(60)
        val second: LongInstant = LongInstant(1)

        fun fromGregorianDateTime(
            gregorianDateTime: GregorianDateTime,
            timeZone: TimeZone,
        ): LongInstant {
            if (gregorianDateTime.date.year !in LOCAL_DATE_WORKING_RANGE) {
                return LongInstant(
                    ((1970 + gregorianDateTime.date.year) * 365.2425f * 24 * 3600).toLong()
                )
            } else {
                val localDateTime =
                    gregorianDateTime.date.toLocalDate()!!.atTime(gregorianDateTime.time)
                return LongInstant(localDateTime.toInstant(timeZone))
            }
        }
    }
}

fun min(a: LongInstant, b: LongInstant): LongInstant {
    return if (a <= b) a else b
}

val LOCAL_DATE_WORKING_RANGE = -99999999L..99999999L


@OptIn(ExperimentalTime::class)
fun Instant.toGregorianDateTime(timeZone: TimeZone): GregorianDateTime {
    if (this.epochSeconds < Instant.DISTANT_PAST.epochSeconds) {
        val days = (this.epochSeconds / 3600 / 24).toLong() // TODO: day since zero vs epoch
        return GregorianDateTime(GregorianDate(days))
    } else if (this.epochSeconds > Instant.DISTANT_FUTURE.epochSeconds) {
        val days = (this.epochSeconds / 3600 / 24).toLong()
        return GregorianDateTime(GregorianDate(days))
    } else {
        val localDateTime = this.toLocalDateTime(timeZone)
        return GregorianDateTime(
            date = GregorianDate(localDateTime.date),
            time = localDateTime.time,
        )
    }
}


fun Float.format(digits: Int): String {
    val integral = this.toInt()
    val decimal = (this - integral) * 10f.pow(digits)
    return "${integral}.${decimal.toInt()}"
}

infix fun ClosedRange<GregorianDateTime>.step(step: DateTimeUnit.DateBased): Sequence<GregorianDateTime> {
    val period = when (step) {
        is DateTimeUnit.DayBased -> DatePeriod(days = step.days)
        is DateTimeUnit.MonthBased -> DatePeriod(months = step.months)
    }
    return generateSequence(this.start) { value ->
        if (value < this.endInclusive) {
            value + period
        } else {
            null
        }
    }
}

fun GregorianDateTime.floor(cycle: DateTimeUnit.DateBased): GregorianDateTime {
    return when (cycle) {
        is DateTimeUnit.DayBased -> {
            val daysSinceEpoch = this.date.toDaysSinceZero()
            GregorianDateTime(GregorianDate.fromDaysSinceZero(daysSinceEpoch - daysSinceEpoch % cycle.days))
        }

        is DateTimeUnit.MonthBased -> {
            val monthsSinceEpoch = this.date.year * 12 + this.date.month - 1
            val months = (monthsSinceEpoch / cycle.months) * cycle.months
            val year = months / 12
            val month = months.mod(12) + 1
            GregorianDateTime(GregorianDate(year, month, 1))
        }
    }
}

